import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  confirmEmailVerification,
  requestEmailVerification,
} from "../../store/slices/authSlice";
import {
  FiMail,
  FiCheckCircle,
  FiAlertCircle,
  FiRefreshCw,
} from "react-icons/fi";
import Button from "../../components/Button/Button";
import "./EmailVerification.css";

const EmailVerification = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { user, isLoading, error } = useSelector((state) => state.auth);

  const [verificationStatus, setVerificationStatus] = useState("pending"); // pending, verifying, success, error, requesting
  const [message, setMessage] = useState("");
  const token = searchParams.get("token");

  useEffect(() => {
    // If token is in URL, automatically verify
    if (token) {
      handleVerifyEmail(token);
    } else if (user && !user.emailVerified) {
      // User is logged in but not verified, show request form
      setVerificationStatus("pending");
    } else if (user && user.emailVerified) {
      // Already verified, redirect to dashboard
      navigate("/");
    }
  }, [token, user, navigate]);

  const handleVerifyEmail = async (verificationToken) => {
    setVerificationStatus("verifying");
    setMessage("Verifying your email...");

    try {
      const result = await dispatch(
        confirmEmailVerification({ token: verificationToken })
      ).unwrap();
      setVerificationStatus("success");
      setMessage("Email verified successfully! Redirecting to dashboard...");

      // Redirect after 2 seconds
      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (err) {
      setVerificationStatus("error");
      setMessage(
        err || "Email verification failed. The link may have expired."
      );
    }
  };

  const handleRequestVerification = async () => {
    setVerificationStatus("requesting");
    setMessage("Sending verification email...");

    try {
      await dispatch(requestEmailVerification()).unwrap();
      setVerificationStatus("success");
      setMessage(
        "Verification email sent! Please check your inbox and click the link to verify your email."
      );
    } catch (err) {
      setVerificationStatus("error");
      setMessage(err || "Failed to send verification email. Please try again.");
    }
  };

  if (verificationStatus === "verifying") {
    return (
      <div className="email-verification-page">
        <div className="verification-container">
          <div className="verification-icon loading">
            <FiRefreshCw size={48} />
          </div>
          <h1>Verifying Email</h1>
          <p>{message}</p>
        </div>
      </div>
    );
  }

  if (verificationStatus === "success") {
    return (
      <div className="email-verification-page">
        <div className="verification-container">
          <div className="verification-icon success">
            <FiCheckCircle size={48} />
          </div>
          <h1>Email Verified!</h1>
          <p>{message}</p>
        </div>
      </div>
    );
  }

  if (verificationStatus === "error") {
    return (
      <div className="email-verification-page">
        <div className="verification-container">
          <div className="verification-icon error">
            <FiAlertCircle size={48} />
          </div>
          <h1>Verification Failed</h1>
          <p>{message}</p>
          {user && !user.emailVerified && (
            <div className="verification-actions">
              <Button onClick={handleRequestVerification} loading={isLoading}>
                <FiMail size={18} />
                Resend Verification Email
              </Button>
              <Button variant="secondary" onClick={() => navigate("/")}>
                Go to Dashboard
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Request verification email form
  return (
    <div className="email-verification-page">
      <div className="verification-container">
        <div className="verification-icon">
          <FiMail size={48} />
        </div>
        <h1>Email Verification Required</h1>
        <p>
          Please verify your email address to continue. We've sent a
          verification link to <strong>{user?.email}</strong>
        </p>
        {error && (
          <div className="error-message">
            <FiAlertCircle size={18} />
            {error}
          </div>
        )}
        <div className="verification-actions">
          <Button
            onClick={handleRequestVerification}
            loading={isLoading || verificationStatus === "requesting"}
          >
            <FiMail size={18} />
            Resend Verification Email
          </Button>
          <Button variant="secondary" onClick={() => navigate("/")}>
            Go to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
